import { ReactNode, useMemo } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar, { AppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';

// project imports
import AppBarStyled from './AppBarStyled';
import HeaderContent from './HeaderContent';
import IconButton from 'components/@extended/IconButton';

import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { MenuOrientation, DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';

// assets
import MenuOutlined from '@ant-design/icons/MenuOutlined';

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

export default function Header() {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { menuOrientation } = useConfig();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // header content
  const headerContent = useMemo(() => <HeaderContent />, []);

  // common header
  const mainHeader: ReactNode = (
    <Toolbar
      sx={{
        minHeight: '48px !important',
        px: { xs: 1.5, sm: 2 }
      }}
    >
      {!isHorizontal ? (
        <IconButton
          aria-label="open drawer"
          onClick={() => handlerDrawerOpen(!drawerOpen, !downLG)}
          edge="start"
          color="secondary"
          variant="light"
          sx={(theme) => ({
            color: 'text.primary',
            bgcolor: drawerOpen ? 'transparent' : 'grey.100',
            ...theme.applyStyles('dark', { bgcolor: drawerOpen ? 'transparent' : 'background.default' }),
            ml: { xs: 0, lg: 0 }
          })}
        >
          <MenuOutlined />
        </IconButton>
      ) : null}
      {headerContent}
    </Toolbar>
  );

  // app-bar params
  const appBar: AppBarProps = {
    position: 'fixed',
    color: 'inherit',
    elevation: 0,
    sx: (theme) => ({
      borderBottom: '1px solid',
      borderBottomColor: 'divider',
      zIndex: 1200,
      // No navegador a var não existe -> 0 (comportamento original). No Electron
      // desce a altura da barra de título para o header ficar logo abaixo dela.
      top: 'var(--tb-offset, 0px)',
      width: isHorizontal
        ? '100%'
        : { xs: '100%', lg: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${MINI_DRAWER_WIDTH}px)` },
      ...(!downLG && {
        margin: theme.spacing(1.5),
        borderRadius: typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius * 2 : theme.spacing(2),
        boxShadow: 'none',
        ...(drawerOpen
          ? {
              marginLeft: `calc(${DRAWER_WIDTH}px + ${theme.spacing(3)})`,
              width: `calc(100% - ${DRAWER_WIDTH}px - ${theme.spacing(4.5)})`
            }
          : {
              marginLeft: `calc(${MINI_DRAWER_WIDTH}px + ${theme.spacing(3)})`,
              width: `calc(100% - ${MINI_DRAWER_WIDTH}px - ${theme.spacing(4.5)})`
            })
      })
    })
  };

  return (
    <>
      {!downLG ? (
        <AppBarStyled open={drawerOpen} {...appBar}>
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  );
}
